// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ToolCase } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ImportsToolsMenuProps {
  // The tools this surface offers, as `DropdownMenuItem`s. Only the chrome lives here because the
  // three imports surfaces (store tab, chain tab, ingress page) don't offer the same set.
  children: ReactNode;
}

export function ImportsToolsMenu({ children }: ImportsToolsMenuProps) {
  const { t } = useTranslation("back-office");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" aria-label={t("imports.tools.menuLabel")}>
          <ToolCase className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* -36 is the negative height of the size-9 trigger, so the menu opens over it; the
          `dropdown-overlap` class swallows the opening click's pointer-up. Both are mandatory. */}
      <DropdownMenuContent align="end" sideOffset={-36} className="dropdown-overlap">
        <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
          <ToolCase className="h-3.5 w-3.5" />
          {t("imports.tools.menuLabel")}
        </DropdownMenuLabel>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
